import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Loader2, Phone, PhoneCall, PhoneForwarded, Search, Settings, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api.js';
import { cn } from '../../lib/utils.js';
import { useBrowserCall } from '../../providers/BrowserCallProvider.js';
import AiCallWizard from './AiCallWizard.js';
import DispositionCapture from './DispositionCapture.js';
import { PARTY_TYPE_DISPLAY_DEFINITIONS } from '../../../../shared/party-types.js';

type CallMode = 'browser' | 'ai' | 'bridge';

interface CallLine {
  id: string;
  label: string;
  provider: 'twilio' | 'vapi';
  number: string | null;
  modes: CallMode[];
  configured: boolean;
  reason?: string;
}

interface BridgeLine {
  id: string;
  label: string;
  phone: string;
  source: 'user' | 'env';
}

interface LinesResponse {
  lines: CallLine[];
  bridgeLines: BridgeLine[];
  defaultBridgeLineId: string | null;
  diagnostics?: {
    browserConfigured: boolean;
    bridgeConfigured: boolean;
    aiConfigured: boolean;
    bridgeCallbackLines: number;
  };
}

interface DialTarget {
  id: string;
  partyId: string;
  partyMasterId?: string | null;
  dealThreadId: string;
  dealTitle: string | null;
  dealPipelineStage?: string | null;
  dealStatus?: string | null;
  partyName: string;
  partyType: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface RawContactResponse {
  partyMasterId: string;
  party: {
    id: string;
    dealThreadId: string;
    partyType: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
  target: DialTarget | null;
  standalone: boolean;
}

interface CallIdentityResolution {
  enabled: boolean;
  identity: { id: string } | null;
  eligible: boolean;
  warnings: string[];
}

function phoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function looksLikePhone(value: string): boolean {
  return phoneDigits(value).length >= 7;
}

function notConfigured(err: unknown, code: string): boolean {
  return err instanceof ApiError
    && (err.status === 503 || (err.data as { error?: string } | undefined)?.error === code);
}

function lineMode(line: CallLine | undefined): CallMode {
  return line?.modes[0] ?? 'browser';
}

export default function GlobalDialer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const browserCall = useBrowserCall();
  const [query, setQuery] = useState('');
  const [selectedTarget, setSelectedTarget] = useState<DialTarget | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [selectedBridgeLineId, setSelectedBridgeLineId] = useState<string | null>(null);
  const [bridgePhone, setBridgePhone] = useState('');
  const [bridgeLabel, setBridgeLabel] = useState('My phone');
  const [rawName, setRawName] = useState('');
  const [rawEmail, setRawEmail] = useState('');
  const [rawDealId, setRawDealId] = useState('');
  /**
   * Plan item 22. This was hardcoded to 'seller' in the request below, so every
   * number captured through the dialer became a seller - and `partyType` drives
   * `personaForPartyType`, so a title company or a lender captured here got
   * called by a seller persona for the rest of its life.
   */
  const [rawPartyType, setRawPartyType] = useState<string>('seller');
  const [status, setStatus] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const [aiTarget, setAiTarget] = useState<DialTarget | null>(null);
  const [aiIdentityId, setAiIdentityId] = useState<string | undefined>();

  const term = query.trim();
  const isRawPhone = looksLikePhone(term);

  const linesQuery = useQuery({
    queryKey: ['global-dialer', 'lines'],
    queryFn: () => api.get<LinesResponse>('/api/global-dialer/lines'),
    enabled: open,
    staleTime: 30_000,
  });
  const lines = linesQuery.data?.lines ?? [];
  const bridgeLines = linesQuery.data?.bridgeLines ?? [];

  const searchQuery = useQuery({
    queryKey: ['global-dialer', 'search', term],
    queryFn: () => api.get<{ targets: DialTarget[] }>(`/api/global-dialer/search?q=${encodeURIComponent(term)}&limit=8`),
    enabled: open && term.length >= 2,
    staleTime: 0,
  });
  const targets = searchQuery.data?.targets ?? [];
  const onlyTarget = targets.length === 1 ? targets[0] : null;

  const selectedLine = useMemo(() => (
    lines.find((line) => line.id === selectedLineId) ?? lines.find((line) => line.configured) ?? lines[0]
  ), [lines, selectedLineId]);
  const selectedBridgeLine = useMemo(() => (
    bridgeLines.find((line) => line.id === selectedBridgeLineId)
    ?? bridgeLines.find((line) => line.id === linesQuery.data?.defaultBridgeLineId)
    ?? bridgeLines[0]
  ), [bridgeLines, linesQuery.data?.defaultBridgeLineId, selectedBridgeLineId]);
  const activePhone = selectedTarget?.phone?.trim() || (isRawPhone ? term : '');
  const canUseDealCall = Boolean(selectedTarget?.dealThreadId && selectedTarget?.partyId && activePhone);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedTarget(null);
      setStatus(null);
      setRawName('');
      setRawEmail('');
      setRawDealId('');
      setRawPartyType('seller');
      setAiTarget(null);
      setAiIdentityId(undefined);
      setBridgePhone('');
      setBridgeLabel('My phone');
      setSelectedBridgeLineId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  useEffect(() => {
    if (selectedLineId || lines.length === 0) return;
    const preferred = lines.find((line) => line.configured) ?? lines[0];
    setSelectedLineId(preferred?.id ?? null);
  }, [lines, selectedLineId]);

  useEffect(() => {
    if (!open || selectedTarget || term.length < 2 || !onlyTarget) return;
    setSelectedTarget(onlyTarget);
  }, [onlyTarget, open, selectedTarget, term.length]);

  useEffect(() => {
    if (!open || bridgeLines.length === 0 || bridgePhone) return;
    const preferred = selectedBridgeLine ?? bridgeLines[0];
    setSelectedBridgeLineId(preferred?.id ?? null);
    setBridgePhone(preferred?.phone ?? '');
    setBridgeLabel(preferred?.source === 'env' ? 'My phone' : preferred?.label ?? 'My phone');
  }, [bridgeLines, bridgePhone, open, selectedBridgeLine]);

  const rawContact = useMutation({
    mutationFn: () => api.post<RawContactResponse>('/api/global-dialer/raw-contact', {
      phone: activePhone,
      name: rawName.trim(),
      email: rawEmail.trim() || undefined,
      dealThreadId: rawDealId.trim() || undefined,
      partyType: rawPartyType,
    }),
    onSuccess: (res) => {
      if (res.target) setSelectedTarget(res.target);
      setStatus({
        kind: 'info',
        text: res.standalone ? 'Contact saved. Browser Call can start now.' : 'Contact linked to deal.',
      });
    },
    onError: () => setStatus({ kind: 'error', text: 'Could not save that contact.' }),
  });

  const saveBridgeLine = useMutation({
    mutationFn: () => {
      const phone = bridgePhone.trim();
      if (!looksLikePhone(phone)) {
        throw new Error('bridge_phone_required');
      }
      const existingUserLines = bridgeLines.filter((line) => line.source === 'user');
      const nextId = selectedBridgeLine?.source === 'user'
        ? selectedBridgeLine.id
        : `bridge-line-${Date.now()}`;
      const nextLine = {
        id: nextId,
        label: bridgeLabel.trim() || 'My phone',
        phone,
      };
      const nextLines = [
        nextLine,
        ...existingUserLines
          .filter((line) => line.id !== nextId)
          .map((line) => ({ id: line.id, label: line.label, phone: line.phone })),
      ].slice(0, 8);
      return api.patch<Pick<LinesResponse, 'bridgeLines' | 'defaultBridgeLineId'>>('/api/global-dialer/preferences', {
        bridgeLines: nextLines,
        defaultBridgeLineId: nextId,
      });
    },
    onSuccess: (res) => {
      queryClient.setQueryData<LinesResponse>(['global-dialer', 'lines'], (current) => ({
        lines: current?.lines ?? lines,
        diagnostics: current?.diagnostics,
        ...res,
      }));
      const preferred = res.bridgeLines.find((line) => line.id === res.defaultBridgeLineId) ?? res.bridgeLines[0];
      setSelectedBridgeLineId(preferred?.id ?? null);
      setBridgePhone(preferred?.phone ?? bridgePhone);
      setBridgeLabel(preferred?.source === 'env' ? 'My phone' : preferred?.label ?? 'My phone');
      setStatus({ kind: 'info', text: 'Bridge line saved.' });
    },
    onError: () => setStatus({ kind: 'error', text: 'Enter a valid bridge callback number.' }),
  });

  const bridge = useMutation({
    mutationFn: ({ staffPhone, target, identityId }: { staffPhone: string; target: DialTarget; identityId?: string }) => api.post('/api/deal-calls/launch-bridge', {
      dealThreadId: target.dealThreadId,
      partyId: target.partyId,
      toNumber: target.phone ?? activePhone,
      staffPhone,
      identityId,
    }),
    onSuccess: () => setStatus({ kind: 'info', text: 'Bridge Call started.' }),
    onError: (err) => setStatus({
      kind: 'error',
      text: notConfigured(err, 'twilio_not_configured')
        ? 'Bridge Call is not fully configured.'
        : 'Bridge Call could not start.',
    }),
  });

  const resolveTargetIdentity = async (target: DialTarget | null): Promise<CallIdentityResolution | null> => {
    if (!target?.dealThreadId) return null;
    const resolved = await api.post<CallIdentityResolution>('/api/call-identities/resolve', {
      dealThreadId: target.dealThreadId,
    });
    if (resolved.enabled && (!resolved.eligible || !resolved.identity)) {
      throw new Error(resolved.warnings[0] ?? 'Calling identity is not configured for this JVP/source.');
    }
    return resolved;
  };

  const ensureRawContact = async (): Promise<{ ok: boolean; target: DialTarget | null }> => {
    if (selectedTarget) return { ok: true, target: selectedTarget };
    if (!isRawPhone) {
      setStatus({ kind: 'error', text: 'Enter a phone number or select a contact.' });
      return { ok: false, target: null };
    }
    if (!rawName.trim()) {
      setStatus({ kind: 'error', text: 'Name is required before calling a new number.' });
      return { ok: false, target: null };
    }
    const res = await rawContact.mutateAsync();
    return { ok: true, target: res.target };
  };

  const startBrowserCall = async () => {
    setStatus(null);
    const ensured = await ensureRawContact();
    if (!ensured.ok) return;
    const targetPhone = ensured.target?.phone?.trim() || activePhone;
    if (!targetPhone) return;
    let resolved: CallIdentityResolution | null = null;
    try {
      resolved = await resolveTargetIdentity(ensured.target);
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
      return;
    }
    if (!browserCall.available) {
      setStatus({ kind: 'error', text: 'Browser Call is not fully configured.' });
      void browserCall.dial(targetPhone, { identityId: resolved?.identity?.id, dealThreadId: ensured.target?.dealThreadId });
      return;
    }
    void browserCall.dial(targetPhone, { identityId: resolved?.identity?.id, dealThreadId: ensured.target?.dealThreadId });
    if (ensured.target?.dealThreadId && ensured.target.partyId) {
      void api.post('/api/deal-calls', {
        dealThreadId: ensured.target.dealThreadId,
        partyId: ensured.target.partyId,
        mode: 'browser',
        toNumber: targetPhone,
        fromNumber: selectedLine?.number ?? undefined,
        identityId: resolved?.identity?.id,
        buyingSignals: { globalDialer: true },
      }).catch(() => {
        setStatus({ kind: 'info', text: 'Browser Call started. Call logging needs attention.' });
      });
    }
    setStatus({ kind: 'info', text: 'Browser Call started.' });
  };

  const startBridgeCall = async () => {
    setStatus(null);
    const ensured = await ensureRawContact();
    const target = ensured.target;
    if (!ensured.ok) return;
    if (!target) return;
    if (!target.dealThreadId || !target.partyId || (!canUseDealCall && !rawDealId.trim())) {
      setStatus({ kind: 'error', text: 'Bridge Call needs a linked deal party.' });
      return;
    }
    const staff = bridgePhone.trim() || selectedBridgeLine?.phone || '';
    if (!looksLikePhone(staff)) {
      setStatus({ kind: 'error', text: 'Bridge Call needs the phone number to ring first.' });
      return;
    }
    let resolved: CallIdentityResolution | null = null;
    try {
      resolved = await resolveTargetIdentity(target);
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
      return;
    }
    bridge.mutate({ staffPhone: staff, target, identityId: resolved?.identity?.id });
  };

  const startAiCall = async () => {
    setStatus(null);
    const ensured = await ensureRawContact();
    const target = ensured.target;
    if (!ensured.ok) return;
    if (!target?.dealThreadId || !target.partyId) {
      setStatus({ kind: 'error', text: 'AI Call needs a linked deal party.' });
      return;
    }
    let resolved: CallIdentityResolution | null = null;
    try {
      resolved = await resolveTargetIdentity(target);
    } catch (error) {
      setStatus({ kind: 'error', text: (error as Error).message });
      return;
    }
    setAiIdentityId(resolved?.identity?.id);
    setAiTarget(target);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.button
              type="button"
              aria-label="Close dialer"
              className="fixed inset-0 z-modal-overlay bg-ink/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Global dialer"
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.14 }}
              className="fixed inset-x-4 top-[8vh] z-modal mx-auto flex h-[88vh] max-h-[88vh] max-w-[780px] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl sm:inset-x-3 sm:top-[6vh] sm:h-auto"
            >
              <div className="flex items-center gap-3 border-b border-line px-4 py-3.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <PhoneCall size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-ink">Dialer</h2>
                  <p className="text-[11px] text-ink-subtle">Search a person, deal, address, or number.</p>
                </div>
                <a
                  href="/admin/wiring"
                  aria-label="Wiring"
                  className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-ink-muted hover:bg-surface-raised hover:text-ink"
                >
                  <Settings size={13} />
                  <span className="hidden sm:inline">Wiring</span>
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-raised hover:text-ink"
                  aria-label="Close dialer"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Item 27. Above the search box on purpose: opening the dialer is
                  the moment somebody is about to make the next call, which is
                  exactly when item 28's rule would stop them. Renders nothing at
                  all when no call is waiting. */}
              <DispositionCapture open={open} />

              <div className="max-h-[calc(88vh-10rem)] space-y-3 overflow-y-auto border-b border-line bg-surface-sunken/35 p-4 sm:max-h-none">
                <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2.5 shadow-sm">
                  <Search size={15} className="text-ink-subtle" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setSelectedTarget(null);
                      setStatus(null);
                    }}
                    placeholder="Name, phone, address, deal"
                    className="min-w-0 flex-1 bg-transparent text-base sm:text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
                  />
                </div>

                <div className="grid gap-2 md:grid-cols-3" aria-label="Call lines">
                  {lines.map((line) => (
                    <button
                      key={line.id}
                      type="button"
                      onClick={() => setSelectedLineId(line.id)}
                      title={line.reason ?? line.label}
                      className={cn(
                        'flex min-h-[3.5rem] min-w-0 items-start gap-2 rounded-xl border px-3 py-2 text-left transition-colors sm:min-h-[4.25rem] sm:py-2.5',
                        selectedLine?.id === line.id
                          ? 'border-brand bg-brand/10 text-brand shadow-sm'
                          : 'border-line bg-surface text-ink-muted hover:border-brand/25 hover:bg-surface-raised',
                        !line.configured && 'opacity-70',
                      )}
                    >
                      <span className={cn(
                        'mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg',
                        selectedLine?.id === line.id ? 'bg-brand text-primary-fg' : 'bg-surface-sunken text-ink-muted',
                      )}>
                        {lineMode(line) === 'ai' ? <Bot size={13} /> : lineMode(line) === 'bridge' ? <PhoneForwarded size={13} /> : <Phone size={13} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-ink">{line.label}</span>
                        <span className="mt-0.5 block truncate text-[11px] text-ink-muted">
                          {lineMode(line) === 'ai' ? 'AI Call' : lineMode(line) === 'bridge' ? 'Bridge Call' : 'Browser Call'}
                          {line.number ? ` ${line.number}` : ''}
                        </span>
                        {!line.configured && (
                          <span className="mt-1 block truncate text-[10px] text-warning">{line.reason ?? 'Needs wiring'}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="rounded-xl border border-line bg-surface p-3 shadow-sm">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                        <PhoneForwarded size={14} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-ink">Bridge callback</p>
                        <p className="truncate text-[11px] text-ink-subtle">The phone Twilio rings before connecting the lead.</p>
                      </div>
                    </div>
                    {linesQuery.data?.diagnostics?.bridgeConfigured === false && (
                      <a href="/admin/wiring" className="flex-shrink-0 text-[11px] font-medium text-brand hover:underline">
                        Check wiring
                      </a>
                    )}
                  </div>

                  {bridgeLines.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {bridgeLines.map((line) => (
                        <button
                          key={line.id}
                          type="button"
                          onClick={() => {
                            setSelectedBridgeLineId(line.id);
                            setBridgePhone(line.phone);
                            setBridgeLabel(line.source === 'env' ? 'My phone' : line.label);
                            setStatus(null);
                          }}
                          className={cn(
                            'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                            selectedBridgeLine?.id === line.id
                              ? 'border-brand bg-brand/10 text-brand'
                              : 'border-line bg-surface text-ink-muted hover:bg-surface',
                          )}
                        >
                          {line.label}
                          <span className="font-mono text-[10px] text-ink-subtle mask-sensitive">{line.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="grid gap-2 sm:grid-cols-[0.8fr_1fr_auto]">
                    <input
                      value={bridgeLabel}
                      onChange={(event) => setBridgeLabel(event.target.value)}
                      placeholder="Line label"
                      className="h-9 rounded-lg border border-line bg-surface px-2.5 text-base sm:text-xs text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                    <input
                      value={bridgePhone}
                      onChange={(event) => setBridgePhone(event.target.value)}
                      placeholder="+1 phone to ring first"
                      inputMode="tel"
                      className="h-9 rounded-lg border border-line bg-surface px-2.5 text-base sm:text-xs text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                    <button
                      type="button"
                      onClick={() => saveBridgeLine.mutate()}
                      disabled={saveBridgeLine.isPending}
                      className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-surface px-3 text-xs font-medium text-ink-muted hover:bg-surface-sunken disabled:opacity-50"
                    >
                      {saveBridgeLine.isPending ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-3">
                {searchQuery.isFetching && (
                  <div className="flex items-center gap-2 px-2 py-3 text-xs text-ink-subtle">
                    <Loader2 size={13} className="animate-spin" />
                    Searching
                  </div>
                )}

                {targets.length > 0 && (
                  <div className="space-y-1">
                    {targets.map((target) => (
                      <button
                        key={target.id}
                        type="button"
                        onClick={() => {
                          setSelectedTarget(target);
                          setRawName('');
                          setRawEmail('');
                          setRawDealId('');
                          setStatus(null);
                        }}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                          selectedTarget?.id === target.id
                            ? 'border-brand bg-brand/10'
                            : 'border-transparent hover:bg-surface-raised',
                        )}
                      >
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand mask-sensitive">
                          {target.partyName.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-ink mask-sensitive">
                            {target.partyName}
                          </div>
                          <div className="truncate text-xs text-ink-muted mask-sensitive">
                            {target.phone || target.email || 'No direct number'} · {target.dealTitle ?? 'Linked deal'}
                          </div>
                        </div>
                        <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-[10px] text-ink-muted">
                          {target.partyType}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {term.length >= 2 && targets.length === 0 && !searchQuery.isFetching && (
                  <div className="rounded-lg border border-line bg-surface-raised p-3">
                    <div className="text-sm font-semibold text-ink">
                      {isRawPhone ? 'New number' : 'No contact found'}
                    </div>
                    {isRawPhone && (
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <input
                          value={rawName}
                          onChange={(event) => setRawName(event.target.value)}
                          placeholder="Name"
                          className="rounded-md border border-line bg-surface px-2.5 py-2 text-base sm:text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand"
                        />
                        <input
                          value={rawEmail}
                          onChange={(event) => setRawEmail(event.target.value)}
                          placeholder="Email"
                          className="rounded-md border border-line bg-surface px-2.5 py-2 text-base sm:text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand"
                        />
                        <input
                          value={rawDealId}
                          onChange={(event) => setRawDealId(event.target.value)}
                          placeholder="Deal id"
                          className="rounded-md border border-line bg-surface px-2.5 py-2 text-base sm:text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand sm:col-span-2"
                        />
                        {/* Item 22. Callable types only: capturing somebody as
                            'internal' would create a contact the dialer then
                            refuses to call. */}
                        <label className="flex items-center gap-2 text-xs text-ink-muted sm:col-span-2">
                          <span className="flex-shrink-0">Contact type</span>
                          <select
                            value={rawPartyType}
                            onChange={(event) => setRawPartyType(event.target.value)}
                            aria-label="Contact type for this new number"
                            className="min-w-0 flex-1 rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand"
                          >
                            {PARTY_TYPE_DISPLAY_DEFINITIONS.filter((d) => d.callable).map((d) => (
                              <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-line bg-surface px-3 py-3">
                {status && (
                  <div className={cn(
                    'mb-2 rounded-md border px-3 py-2 text-xs',
                    status.kind === 'error'
                      ? 'border-danger/30 bg-danger/10 text-danger-fg'
                      : 'border-info/30 bg-info-bg text-info',
                  )}
                  >
                    {status.text}
                  </div>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 rounded-lg bg-surface-sunken px-3 py-2 text-xs text-ink-subtle mask-sensitive">
                    {selectedTarget
                      ? `${selectedTarget.partyName} · ${activePhone || 'No number'}`
                      : activePhone || 'Select a contact or enter a number'}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:flex">
                    <button
                      type="button"
                      onClick={() => void startBrowserCall()}
                      disabled={!activePhone || rawContact.isPending}
                      className="inline-flex h-9 min-w-0 items-center justify-center gap-1 rounded-lg border border-line px-1.5 text-[11px] font-medium text-ink-muted hover:border-brand/25 hover:bg-surface-raised hover:text-ink disabled:opacity-40 sm:gap-1.5 sm:px-3 sm:text-xs"
                    >
                      <Phone size={12} className="shrink-0" />
                      <span className="whitespace-nowrap">Browser Call</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void startAiCall()}
                      disabled={!activePhone || rawContact.isPending}
                      className="inline-flex h-9 min-w-0 items-center justify-center gap-1 rounded-lg border border-line px-1.5 text-[11px] font-medium text-ink-muted hover:border-brand/25 hover:bg-surface-raised hover:text-ink disabled:opacity-40 sm:gap-1.5 sm:px-3 sm:text-xs"
                    >
                      <Bot size={12} className="shrink-0" />
                      <span className="whitespace-nowrap">AI Call</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void startBridgeCall()}
                      disabled={!activePhone || rawContact.isPending || bridge.isPending}
                      className="inline-flex h-9 min-w-0 items-center justify-center gap-1 rounded-lg border border-line px-1.5 text-[11px] font-medium text-ink-muted hover:border-brand/25 hover:bg-surface-raised hover:text-ink disabled:opacity-40 sm:gap-1.5 sm:px-3 sm:text-xs"
                    >
                      {bridge.isPending ? <Loader2 size={12} className="shrink-0 animate-spin" /> : <PhoneForwarded size={12} className="shrink-0" />}
                      <span className="whitespace-nowrap">Bridge Call</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AiCallWizard
        dealId={aiTarget?.dealThreadId ?? ''}
        partyId={aiTarget?.partyId}
        partyPhone={aiTarget?.phone ?? activePhone}
        identityId={aiIdentityId}
        open={Boolean(aiTarget)}
        onClose={() => setAiTarget(null)}
      />
    </>
  );
}
